import { BodyPrimary } from '@liveauctioneers/hammer-ui-core/text';
import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { ClockCountdown } from '@liveauctioneers/hammer-ui-core/icons';
import { getCatalogStartTime } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getIsRegistered } from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import { longFormatDate } from '@liveauctioneers/caterwaul-components/lib/utils/dates';
import { openRegisterForAuctionModal } from '@/redux/modules/modal';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import styled from 'styled-components';

export type TimedPlusItemBiddingNotStartedProps = {
    catalogId: number;
    itemId: number;
};

const TimedPlusItemBiddingNotStarted = ({ catalogId, itemId }: TimedPlusItemBiddingNotStartedProps) => {
    const { formatMessage } = useIntl();
    const dispatch = useAppDispatch();
    const startTime = useAppSelector((state) => getCatalogStartTime(state, catalogId));
    const isRegistered = useAppSelector((state) => getIsRegistered(state, catalogId));

    return (
        <StyledContent>
            <div className="hui-text-h5 mb-0 flex items-center uppercase text-text-secondary">
                <ClockCountdown
                    className="mr-xs"
                    size="sm"
                    style="bold"
                />
                {formatMessage({
                    id: 'itemPage.biddingInfo.timedPlus.notStarted.header',
                })}
            </div>
            <StyledDescription>
                <BodyPrimary color="secondary">
                    {formatMessage(
                        {
                            id: 'itemPage.biddingInfo.timedPlus.notStarted.description',
                        },
                        { date: longFormatDate(startTime) }
                    )}
                </BodyPrimary>
            </StyledDescription>
            {!isRegistered && (
                <>
                    <StyledSeparator />
                    <StyledRegisterButton
                        onClick={() => {
                            dispatch(
                                openRegisterForAuctionModal({
                                    catalogId,
                                    itemId,
                                })
                            );
                        }}
                    >
                        {formatMessage({
                            id: 'itemPage.biddingInfo.timedPlus.notStarted.registerForAuction',
                        })}
                    </StyledRegisterButton>
                </>
            )}
        </StyledContent>
    );
};

export default TimedPlusItemBiddingNotStarted;

const StyledContent = styled.div`
    flex: 1 1 auto;
    padding: 32px;
    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        padding: 24px;
    }
    @media print {
        padding: 0;
    }
    & > a {
        display: block;
    }
    & > *:last-child {
        margin-bottom: 0;
    }
    > div:not(:first-child),
    a {
        @media print {
            display: none;
        }
    }
    > div:first-child {
        @media print {
            margin: 0;
        }
    }
`;

const StyledDescription = styled.div`
    margin-top: 8px;
`;

const StyledSeparator = styled.hr`
    border: none;
    border-bottom: 1px solid ${({ theme }) => theme.colors.grey400};
    margin: 24px 0;
`;

const StyledRegisterButton = styled(Button)`
    width: 100%;
    text-transform: uppercase;
`;
